import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_LIST_SORT } from "../../../Components/NewPurchaseOrder/constants";

export const SCROLL_STORAGE_KEY = "new-po-listing-scroll-top";
export const FILTERS_STORAGE_KEY = "new-po-listing-filters";

export const DEFAULT_LISTING_FILTERS = {
  query: "",
  statusFilter: "all",
  vendorFilter: "all",
  sortCol: DEFAULT_LIST_SORT.col,
  sortOrd: DEFAULT_LIST_SORT.ord,
  // Location filter: comma-separated merchant_ids ("" = the logged-in store)
  // and the merchant they were picked under, so another session's picks are
  // never reapplied.
  storeIds: "",
  storeIdsMerchantId: "",
};

const readStoredScroll = () => {
  try {
    const v = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
};

/** Saved filters, kept only where each value is still a string. */
const readStoredFilters = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(FILTERS_STORAGE_KEY) || "{}");
    return Object.keys(DEFAULT_LISTING_FILTERS).reduce(
      (filters, key) => ({
        ...filters,
        [key]: typeof saved?.[key] === "string" ? saved[key] : filters[key],
      }),
      DEFAULT_LISTING_FILTERS
    );
  } catch {
    return DEFAULT_LISTING_FILTERS;
  }
};

/**
 * UI-only state for the new PO listing page (not server data).
 * React Query owns list/count/vendors; Redux owns scroll restoration and the
 * list's search / status / vendor / location / sort, so returning from a PO
 * shows the same list at the same position.
 */
const initialState = {
  scrollTop: readStoredScroll(),
  filters: readStoredFilters(),
};

const purchaseOrderListingUiSlice = createSlice({
  name: "purchaseOrderListingUi",
  initialState,
  reducers: {
    setListingScrollTop: (state, action) => {
      state.scrollTop = Math.max(0, Number(action.payload) || 0);
      try {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, String(state.scrollTop));
      } catch {
        /* ignore quota / private mode */
      }
    },
    setListingFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      try {
        sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(state.filters));
      } catch {
        /* ignore quota / private mode */
      }
    },
    clearListingScrollTop: (state) => {
      state.scrollTop = 0;
      try {
        sessionStorage.removeItem(SCROLL_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    },
  },
});

export const { setListingScrollTop, setListingFilters, clearListingScrollTop } =
  purchaseOrderListingUiSlice.actions;

export default purchaseOrderListingUiSlice.reducer;
